import { FC } from "react";
import { Link, useLocation, useRouteMatch } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  Menu,
  BulbOutlined,
  RightOutlined,
  NotificationOutlined,
  CompassOutlined,
  MessageOutlined,
  ExportOutlined,
  Grid,
  InfoCircleOutlined,
  MailOutlined
} from "@ctra/components";

import { Session } from "@ctra/api";
import { useGoogleAnalytics } from "@ctra/analytics";
import { Enterprise as Content, Enterprise, useTranslation } from "@ctra/i18n";

import { Routes } from "@routes";
import {
  UserPreferences,
  UserPreferencesVariant,
  intercomLauncherClassName,
  useUserPreferences
} from "@base";

import { GAActions as BaseGAActions } from "../../../base";
import { GAActions as AuthGAActions } from "@auth";
import { GAActions } from "../../analytics";
import styles from "./SettingsMenu.module.less";

/**
 * Sidebar menu for the setting page
 * @constructor
 */
export const SettingsMenu: FC = () => {
  const routes = Routes.app.settings;
  const insights = useRouteMatch(`${routes.insights.index}*`);
  const gamePlans = useRouteMatch(`${Routes.app.gamePlans.index}*`);
  const farmSummaries = useRouteMatch(`${Routes.app.farmSummaries.index}*`);
  const referral = useRouteMatch(`${routes.users.index}*`);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { md } = Grid.useBreakpoint();
  const { trackEvent } = useGoogleAnalytics();
  const {
    preferences: { sandbox }
  } = useUserPreferences();

  const {
    settings: { navigation, logout, type }
  } = Enterprise;

  return (
    <Menu mode="inline" selectedKeys={[pathname]} className={styles.Menu}>
      <Menu.ItemGroup>
        {sandbox.isEnabled ? null : (
          <Menu.Item icon={<MailOutlined />} key={referral?.url || "referral"}>
            <Link
              onClick={() => {
                /* istanbul ignore next */
                trackEvent(GAActions.goToReferral);
              }}
              to={routes.users.index}
            >
              {t<string>(navigation.users)}
            </Link>
          </Menu.Item>
        )}
        {!md && (
          <Menu.Item icon={<CompassOutlined />} key={gamePlans?.url || "game-plans"}>
            <Link
              onClick={() => {
                /* istanbul ignore next */
                trackEvent(BaseGAActions.goToGamePlans);
              }}
              to={Routes.app.gamePlans.index}
            >
              {t<string>(Enterprise.navigation.main.gamePlans)}
            </Link>
          </Menu.Item>
        )}
        {!md && (
          <Menu.Item icon={<MailOutlined />} key={farmSummaries?.url || "farm-summaries"}>
            <Link
              onClick={() => {
                /* istanbul ignore next */
                trackEvent(BaseGAActions.goToSummaries);
              }}
              to={Routes.app.farmSummaries.index}
            >
              {t<string>(Enterprise.navigation.main.farmSummaries)}
            </Link>
          </Menu.Item>
        )}
      </Menu.ItemGroup>
      <Menu.ItemGroup title={t<string>(type.insight)}>
        <Menu.Item icon={<BulbOutlined />} key={insights?.url}>
          <Link
            to={routes.insights.index}
            onClick={() => {
              /* istanbul ignore next */
              trackEvent(GAActions.goToInsights);
            }}
          >
            {t<string>(navigation.insights)}
          </Link>
          {!md && <RightOutlined />}
        </Menu.Item>
        <Menu.Item icon={<NotificationOutlined />} key={routes.notifications}>
          <Link to={routes.notifications}>{t<string>(navigation.notifications)}</Link>
          {!md && <RightOutlined />}
        </Menu.Item>
      </Menu.ItemGroup>
      {!md && (
        <Menu.ItemGroup title={t<string>(type.help)}>
          <Menu.Item
            icon={<MessageOutlined />}
            key={"support"}
            className={intercomLauncherClassName}
            onClick={() => {
              /* istanbul ignore next */
              window.Intercom("show");
            }}
          >
            {t<string>(Enterprise.navigation.main.support)}
            <RightOutlined />
          </Menu.Item>
          <Menu.Item icon={<InfoCircleOutlined />} key={Routes.app.helpCenter.index}>
            <a
              href="https://intercom.help/connecterra"
              rel="noopener noreferrer"
              onClick={() => {
                window.open("https://intercom.help/connecterra", "_blank");
                /* istanbul ignore next */
                trackEvent(BaseGAActions.goToHelpCenter);
              }}
              target={"_blank"}
            >
              {t<string>(Content.navigation.main.helpCenter)}
            </a>
          </Menu.Item>
        </Menu.ItemGroup>
      )}
      <Menu.ItemGroup title={t<string>(navigation.account)}>
        {!md && (
          <Link to={routes.preferences}>
            <UserPreferences variant={UserPreferencesVariant.settings} />
          </Link>
        )}
        <Menu.Item
          icon={<ExportOutlined />}
          key={routes.logout}
          onClick={() => {
            dispatch(Session.actions.logout());
            /* istanbul ignore next */
            trackEvent(AuthGAActions.logout);
          }}
        >
          {t<string>(logout)}
          {!md && <RightOutlined />}
        </Menu.Item>
      </Menu.ItemGroup>
    </Menu>
  );
};
